import { SuccessFetchListResponse } from "@/services/api";
import {
  DataTableFeatures,
  UseDataTableOptions,
} from "@/types/dataTableSettings";
import {
  InfiniteData,
  UseInfiniteQueryResult,
  UseQueryResult,
} from "@tanstack/react-query";
import {
  ColumnDef,
  OnChangeFn,
  RowSelectionState,
} from "@tanstack/react-table";
import { TFunction } from "i18next";
import { PropsWithChildren, useMemo } from "react";
import { ZodObject, ZodRawShape, ZodReadonly } from "zod";
import { defaultDataTableFeatures } from "../data-table/DataTableSettingsContext";

interface QueryTableBreadcrumbItem {
  label: string;
  href?: string;
}

export interface QueryTableProps<T> extends PropsWithChildren {
  schema: ZodReadonly<ZodObject<ZodRawShape>>;
  name: string;
  breadcrumbs?: QueryTableBreadcrumbItem[];
  breadcrumbLabel?: string;
  translationKey?: string;
  tableFeatures?: DataTableFeatures;
  headerActions?: (t: TFunction) => React.ReactNode[];
  columnOverrides?: Record<string, ColumnDef<T, number>>;
  query:
    | UseInfiniteQueryResult<InfiniteData<SuccessFetchListResponse<T>>, unknown>
    | UseQueryResult<SuccessFetchListResponse<T>>;
  options: UseDataTableOptions<T>;
  dataMapper?: (data: unknown[]) => T[];
  pathBuilder?: (row: T) => string;
  rowSelection?: RowSelectionState;
  setRowSelection?: OnChangeFn<RowSelectionState>;
}

export function useMergedListFeatures(tableFeatures?: DataTableFeatures) {
  return useMemo(() => {
    const merged: DataTableFeatures = {
      ...defaultDataTableFeatures,
      ...tableFeatures,
    };
    if (merged.enableOnlySelectedFilter && !merged.enableRowSelection) {
      merged.enableOnlySelectedFilter = false;
    }
    return merged;
  }, [tableFeatures]);
}

export function useRawTableRows<T extends { id: string }>(
  query: QueryTableProps<T>["query"],
): T[] {
  return useMemo(() => {
    if (!query.data) return [];
    if ("pages" in query.data)
      return query.data.pages.flatMap(({ items }) => items);
    return query.data.items;
  }, [query.data]);
}
